import React, { useContext } from 'react'
import classNames from 'classnames'
import { Arrow, Download, ChevronDown } from './TextLinkIcons/TextLinkIcons'
import Typography from '@toasttab/shared/src/components/atoms/Typography/Typography'
import { I18nextContext } from '@toasttab/shared/src/context/i18nextContext'
import { localizeLink } from '@toasttab/shared/src/utils/linkUtils'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import { TextLinkProps } from './TextLink.d'

export const handleScroll = (
  event:
    | React.MouseEvent<HTMLAnchorElement, MouseEvent>
    | React.MouseEvent<HTMLSpanElement, MouseEvent>,
  targetId: string
) => {
  event.preventDefault()
  const targetElement = document.getElementById(targetId)

  targetElement?.setAttribute('tabindex', '-1')

  _helpers.smoothScroll(targetId)
  if (window.history.pushState) {
    window.history.pushState(null, null, `#${targetId}`)
    window.dispatchEvent(new HashChangeEvent('hashchange'))
  } else {
    window.location.hash = `#${targetId}`
    window.dispatchEvent(new HashChangeEvent('hashchange'))
  }

  setTimeout(() => {
    targetElement?.focus()
    targetElement?.removeAttribute('tabindex')
  }, 500)
}

const TextLink = ({
  allowHtml,
  classes,
  color = 'default',
  description,
  el = 'a',
  fontSize = ['text-base'],
  fontWeight = ['font-semibold'],
  heading,
  heapInteraction,
  href = '',
  icon,
  iconClass,
  isDisplayedBlock,
  numComponents = 1,
  tabIndex,
  target,
  text,
  underline,
  underlineHover,
  unlinkedText,
  wrapperClasses
}: TextLinkProps) => {
  const context = useContext(I18nextContext)
  const linkHref = localizeLink(href, context.language, context)

  const Element = el

  const wrapClasses = classNames(wrapperClasses, {
    'mt-6': !wrapperClasses
  })
  const linkTextClasses = classNames(classes, 'font-sansPro leading-[36px]', {
    [fontSize.join(' ')]: fontSize,
    [fontWeight.join(' ')]: fontWeight,
    block: heading,
    'group inline-flex items-center': icon,
    'text-blue-75 text-primary-blue-75 hover:text-blue-100':
      color === 'default',
    'text-white hover:text-gray-30': color === 'light',
    underline: underline,
    'relative w-fit hover:after:content_[""] hover:after:inline-flex hover:after:absolute hover:after:bottom-0 hover:after:w-full hover:after:h-[1px]':
      underlineHover,
    'hover:after:bg-blue-100': underlineHover && color === 'default',
    'hover:after:bg-gray-30': underlineHover && color === 'light'
  })

  const iconClasses = classNames(iconClass, {
    'ml-1 transition-all group-hover:translate-x-[7px]': icon,
    'stroke-blue-75 group-hover:stroke-blue-100':
      (icon === 'arrow' || icon === 'arrowRight' || icon === 'chevron-down') &&
      color === 'default',
    'stroke-white group-hover:stroke-gray-30':
      (icon === 'arrow' || icon === 'arrowRight' || icon === 'chevron-down') &&
      color === 'light',
    'fill-blue-75 group-hover:fill-blue-100':
      icon === 'download' && color === 'default',
    'fill-white group-hover:fill-gray-30':
      icon === 'download' && color === 'light'
  })

  const renderComponent = (inlineHeading: boolean) => {
    const headingClasses = classNames('text-gray-100  pt-1', {
      block: !inlineHeading,
      'inline block pr-4': inlineHeading
    })

    return (
      <>
        {heading ? (
          <Typography as='p' classes={headingClasses} variation='default'>
            {heading}
          </Typography>
        ) : null}
        <Element
          aria-label={description ?? ''}
          className={linkTextClasses}
          data-interaction={heapInteraction ?? ''}
          data-testid={`text-link${numComponents ? `-${numComponents}` : ''}`}
          {...(linkHref ? { href: linkHref } : {})}
          rel={target === '_blank' ? 'noopener noreferrer' : undefined}
          tabIndex={tabIndex}
          target={target}
          onClick={(ev) => {
            ev.stopPropagation()
            if (linkHref && linkHref.startsWith('#')) {
              const targetId = linkHref.replace('#', '')
              handleScroll(ev, targetId)
            }
          }}
        >
          {unlinkedText ? (
            <span className='inline-block mr-4 font-normal text-secondary'>
              {unlinkedText}
            </span>
          ) : null}
          {allowHtml ? (
            <span dangerouslySetInnerHTML={{ __html: text }} />
          ) : (
            text
          )}
          {(icon === 'arrow' || icon === 'arrowRight') && (
            <Arrow classes={iconClasses} />
          )}
          {icon === 'download' && <Download classes={iconClasses} />}
          {icon === 'chevron-down' && <ChevronDown classes={iconClasses} />}
        </Element>
      </>
    )
  }

  return (
    <>
      {isDisplayedBlock ? (
        <div className={wrapClasses} data-testid='text-link-wrapper'>
          {renderComponent(true)}
        </div>
      ) : null}
      {!isDisplayedBlock && renderComponent(false)}
    </>
  )
}

export default TextLink
